import { Battery } from 'src/Types/responses/storages/serialNo/hardware/batteries'
import { majorVersionFromResponse } from 'src/Libs/system-data-handler/utils/versions'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { AuthFetchWithResponse } from 'src/Types/api'
import { BatteryOverviewDto, BatterySettingsDto } from 'src/Libs/batteries'
import { ApiResult } from 'src/Libs/api'
import type { BatteryModel, BatteryPowerManagementModel } from '../models'
import { fromDtoToModel } from '../transformers'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { BatteryInfo, BatteryTrainingModel } from 'src/Libs/system-data-handler'
import { BatteryHealthStatusDto } from './dto/battery-status.dto'

/**
 * Returns battery details stored in the database
 *
 * @param storageId
 * @param authFetch
 */
export const fetchBatteries = async (storageId: string, authFetch: AuthFetchWithResponse): Promise<BatteryInfo> => {
  const batteriesResponse = await authFetch<Battery[]>({
    path: `storages/${storageId}/hardware/batteries`,
    method: 'GET',
  })

  return {
    version: majorVersionFromResponse(batteriesResponse),
    batteries: batteriesResponse.data,
  }
}

type BatteryListItem = {
  storageSerialNo: string
  batteryIndex: number
}

/**
 * Returns all available battery-references for a storage, from the RSCP-broker
 * The returned information comes directly from the device.
 *
 * @param storageId
 * @param authFetch
 * @param abortSignal
 */

export const fetchRscpBatteries = async (
  storageId: string,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<BatteryListItem[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<{ ids: number[] }>({
      path: `storages/${storageId}/hardware/batteries/overview`,
      method: 'GET',
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      const transformedData = response.data.ids.map(batteryIndex => ({
        storageSerialNo: storageId,
        batteryIndex,
      }))

      return new ApiResult<BatteryListItem[], AuthFetchError | Error>(transformedData, false)
    }
    return new ApiResult<BatteryListItem[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<BatteryListItem[]>(error)
  }
}

/**
 * Returns all available battery-references for a farm storage, from the RSCP-broker
 * The returned information comes directly from the device.
 *
 * @param storageId
 * @param authFetch
 * @param abortSignal
 */

export const fetchRscpBatteriesMaster = async (
  storageId: string,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<BatteryListItem[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<{ [key: number]: number[] }>({
      path: `storages/${storageId}/hardware/batteries/overview/master`,
      method: 'GET',
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      const transformedData = Object.entries(response.data).flatMap(([storageSerialNo, batteryIndexes]) => {
        return batteryIndexes.map(batteryIndex => ({
          storageSerialNo,
          batteryIndex,
        }))
      })

      return new ApiResult<BatteryListItem[], AuthFetchError | Error>(transformedData, false)
    }
    return new ApiResult<BatteryListItem[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<BatteryListItem[]>(error)
  }
}

/**
 * Returns battery settings for a single storage, from the RSCP-broker.
 * The returned information comes directly from the device.
 *
 * @param storageId
 * @param batteryIndex The not the ID, index of the list of batteries
 * @param authFetch
 * @param abortSignal
 */
export const fetchRscpBattery = async (
  storageId: string,
  batteryIndex: number,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<BatteryModel>> => {
  try {
    const response = await authFetch<BatteryOverviewDto>({
      path: `storages/${storageId}/hardware/batteries/overview/${batteryIndex}`,
      method: 'GET',
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      const model = fromDtoToModel(response.data as BatteryOverviewDto)
      return new ApiResult<BatteryModel>(model)
    }
    return new ApiResult<BatteryModel>(null, true)
  } catch (error) {
    return new ApiResult<BatteryModel>(null, true)
  }
}

/**
 * Updates the settings for a single battery directly on the device.
 *
 * @param storageSerialNr
 * @param batteryId
 * @param values
 * @param authFetch
 */
export const updateRscpBatterySettings = async (
  storageSerialNr: string,
  batteryId: string,
  values: BatterySettingsDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean>> => {
  const response = await authFetch<void>({
    path: `storages/${storageSerialNr}/hardware/batteries/overview/${batteryId}`,
    method: 'PUT',
    payload: JSON.stringify(values),
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
  return new ApiResult<boolean>(true, false)
}

/**
 * Returns training status for specific battery
 *
 * @param storageId
 * @param authFetch
 */
export const fetchBatteryTrainingMode = async (
  storageId: string,
  batteryId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<BatteryTrainingModel>> => {
  try {
    const response = await authFetch<BatteryTrainingModel>({
      path: `storages/${storageId}/hardware/battery-trainings/${batteryId}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<BatteryTrainingModel>(response.data, false)
    }
    return new ApiResult<BatteryTrainingModel>(null, false)
  } catch (error) {
    return handleFetchError<BatteryTrainingModel>(error)
  }
}

export const updateBatteryTrainingMode = async (
  storageId: string,
  batteryId: string,
  authFetch: AuthFetchWithResponse,
  trainingMode: boolean
): Promise<void> => {
  const response = await authFetch<void>({
    path: `storages/${storageId}/hardware/battery-trainings/${batteryId}`,
    method: 'PUT',
    payload: { trainingMode },
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}

export const getBatteryPowerManagementByStorage = async (
  storageId: string | undefined,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<BatteryPowerManagementModel[], Error | AuthFetchError>> => {
  try {
    const response = await authFetch<BatteryPowerManagementModel[]>({
      path: `storages/${storageId}/hardware/battery-managements`,
      method: 'GET',
      payload: undefined,
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<BatteryPowerManagementModel[], Error | AuthFetchError>(response.data, false)
    }

    return new ApiResult<BatteryPowerManagementModel[], Error | AuthFetchError>(null, true)
  } catch (error) {
    console.error(error)
    return handleFetchError<BatteryPowerManagementModel[]>(error)
  }
}

export const fetchBatteryHealtStatusByIndex = async (
  storageId: string | undefined,
  batteryIndex: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<BatteryHealthStatusDto[]>> => {
  if (storageId === undefined) {
    return new ApiResult<BatteryHealthStatusDto[]>(null, true, new Error('Storage ID is not provided'))
  }

  if (batteryIndex === undefined) {
    return new ApiResult<BatteryHealthStatusDto[]>(null, true, new Error('Battery Index is not provided'))
  }

  try {
    const response = await authFetch<BatteryHealthStatusDto[]>({
      path: `storages/${storageId}/hardware/batteries/overview/${batteryIndex}/battvo`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<BatteryHealthStatusDto[]>(response.data, false)
    }

    return new ApiResult<BatteryHealthStatusDto[]>(null, true)
  } catch (error) {
    console.error(error)
    return handleFetchError<BatteryHealthStatusDto[]>(error)
  }
}
